/**
 * The editor's layout vocabulary: the factories a draft is built and normalised with.
 *
 * Upstream these live in `m02-mockdb.js` §canvas/arka plan/katman (`makeLayer`, `textLayer`,
 * `qrLayer`, `makeField`, `makeSafeArea`, `makeBackground`, `stripDraft`) with the editor's own
 * accessors on top of them in `m08-admin.js:164-169`. They are one file here because the editor is
 * their only remaining caller in the rebuild, and because every default below is load-bearing: a
 * layer minted with `weight: 600` instead of the reference's is a visible pixel difference the
 * moment it is dropped on the stage.
 *
 * Nothing here renders. The stage draws through `lib/render.ts`, which types the same objects as
 * its own `Layout`; `asRenderLayout` is the one bridge between the two spellings.
 */
import constants from '../data/demo-constants.json';
import type { DemoDraft, DemoFieldDefinition, DemoRelease, DemoTemplate } from '../data/types';
import type { EditorLayer, EditorLayout, EditorSafeArea } from '../screens/editor/contract';
import { seedFonts } from './asset-library';
import { sampleData } from './render';
import type { Layout } from './render/engine';

/** `holderFieldKey` already exists on the render path; the editor reads the same one. */
export { holderFieldKey } from './render';

// ----------------------------------------------------------------------------------- identities

/**
 * `uid` — `m01-utils.js:18`, a monotonic base-36 counter starting at 1000. No randomness and no
 * clock, so a demo session mints the same ids in the same order every load.
 */
let uidCounter = 1000;
export const uid = (prefix = 'id'): string => `${prefix}_${(++uidCounter).toString(36)}`;

/** `UID_SUFFIX_RX` — `m01-utils.js:25`. Content-addressed ids (`asset_<24 hex>`) never match. */
const UID_SUFFIX_RX = /^[a-z]+_([0-9a-z]{1,8})$/;

/** `bumpUidFloor` — `m01-utils.js:26`. Raises the counter past an id that was not minted from it. */
export function bumpUidFloor(id: unknown): void {
  const m = UID_SUFFIX_RX.exec(String(id ?? ''));
  const suffix = m?.[1];
  if (!suffix) return;
  const n = Number.parseInt(suffix, 36);
  if (!Number.isFinite(n) || n > 1e12) return;
  if (n > uidCounter) uidCounter = n;
}

/** `deepClone` — `m01-utils.js:40`. JSON round-trip, so an `undefined` field is dropped as upstream. */
const deepClone = <T>(o: T): T => JSON.parse(JSON.stringify(o)) as T;

// ------------------------------------------------------------------------------ field vocabulary

/** A catalogue entry, or any partly-filled field `makeField` is asked to complete. */
export interface FieldSeed {
  key: string;
  tr?: string;
  en?: string;
  labels?: { tr?: string; en?: string } | null;
  dataType?: string;
  required?: boolean;
  importable?: boolean;
  visibility?: string;
  defaultValue?: unknown;
  normalization?: string[];
  validation?: Record<string, unknown>;
  sample?: string;
  auto?: boolean;
  role?: string | null;
}

/** `FIELD_CATALOG` — `m02-mockdb.js:52`, in the reference's order; it is the «Alan ekle» order too. */
export const FIELD_CATALOG = constants.FIELD_CATALOG as unknown as FieldSeed[];

/** `catalogField` — a key outside the catalogue is a custom field, not an error. */
export const catalogField = (k: string): FieldSeed | null => FIELD_CATALOG.find((f) => f.key === k) ?? null;

/** `VISIBILITY` — `m01c-validate.js:8`. Anything else is forced to `private`, never guessed. */
const VISIBILITY = ['private', 'public_metadata', 'public_render', 'public_metadata_and_render'] as const;

/** `SYSTEM_PRIVATE_KEYS` — `m01c-validate.js:13`. These may never be made public, by any caller. */
const SYSTEM_PRIVATE_KEYS = [
  'email',
  'claim_token',
  'claim_token_hash',
  'internal_id',
  'batch_id',
  'admin_actor',
  'source_row_hash',
  'private_contact_email',
];
const isSystemPrivate = (key: string): boolean => SYSTEM_PRIVATE_KEYS.includes(key);

/**
 * `makeField` — `m02-mockdb.js:82`.
 *
 * `over` wins over both the catalogue and the seed for every field EXCEPT visibility: a system
 * field and an e-mail field are re-forced to `private` after the merge, so no call site can widen
 * them by passing `visibility` (rule 12 — a pending record must not leak personal data).
 */
export function makeField(
  src: string | FieldSeed,
  over: Partial<DemoFieldDefinition> = {},
): DemoFieldDefinition {
  const base: FieldSeed =
    typeof src === 'string'
      ? (catalogField(src) ?? { key: src, tr: src, en: src, dataType: 'string', sample: src })
      : src;
  const auto = over.auto !== undefined ? over.auto : !!base.auto;
  const key = base.key;
  const sysPrivate = isSystemPrivate(key);
  const vis = over.visibility || base.visibility || 'public_metadata_and_render';
  const f: DemoFieldDefinition = {
    id: uid('fld'),
    key,
    labels: {
      tr: base.tr || base.labels?.tr || key,
      en: base.en || base.labels?.en || base.tr || key,
    },
    dataType: base.dataType || 'string',
    required: base.required ?? false,
    importable: base.importable ?? !auto,
    visibility: vis as DemoFieldDefinition['visibility'],
    defaultValue: base.defaultValue ?? '',
    normalization: base.normalization || [],
    validation: deepClone(base.validation || {}),
    sample: base.sample || '',
    auto,
    role: base.role || null,
    custom: !catalogField(key),
    ...over,
  };
  if (sysPrivate) f.visibility = 'private';
  if (f.dataType === 'email') f.visibility = 'private';
  if (!VISIBILITY.includes(f.visibility)) f.visibility = 'private';
  return f;
}

// --------------------------------------------------------------------- background, layer, safe area

export interface EditorAssetRef {
  assetId?: string | undefined;
  sha256?: string | undefined;
}

export interface EditorBackground {
  assetRef: EditorAssetRef | null;
  /**
   * Whether the chosen artwork composites over the page, recorded where it is KNOWN.
   *
   * The public page decides the artwork's width from this: an image with alpha is a badge drawn
   * over the panel's own ground and keeps the 480px cap; an opaque one is a certificate sheet and
   * takes the full panel (`credVisualClass`). Only the editor can answer it — it has the decoded
   * asset in hand — and the answer has to survive into the immutable release, because a visitor
   * on `/c/:code` never loads the asset library and the backend stores no alpha column. A
   * template released before this field existed simply omits it, and the page falls back to the
   * certificate width exactly as it did.
   */
  hasAlpha?: boolean;
  fitMode: string;
  x: number;
  y: number;
  scale: number;
  crop: { x: number; y: number; width: number; height: number } | null;
  focal: { x: number; y: number } | null;
  rotation: number;
  opacity: number;
}

/** `FIT_MODES` — `m02-mockdb.js:117`. «Esnet» is last because it is the one that distorts. */
export const FIT_MODES = constants.FIT_MODES as { key: string; tr: string }[];

/**
 * `makeBackground` — `m02-mockdb.js:128`. The asset reference is rebuilt field by field rather
 * than kept, so a background can never carry anything but `assetId` + `sha256` into a release.
 */
export function makeBackground(src?: Partial<EditorBackground> | null): EditorBackground {
  const o = src || {};
  return {
    assetRef: o.assetRef ? { assetId: o.assetRef.assetId, sha256: o.assetRef.sha256 } : null,
    ...(o.hasAlpha === undefined ? {} : { hasAlpha: o.hasAlpha }),
    fitMode: o.fitMode || 'contain',
    x: o.x ?? 0.5,
    y: o.y ?? 0.5,
    scale: o.scale ?? 1,
    crop: o.crop ? { x: o.crop.x, y: o.crop.y, width: o.crop.width, height: o.crop.height } : null,
    focal: o.focal ? { x: o.focal.x, y: o.focal.y } : null,
    rotation: o.rotation ?? 0,
    opacity: o.opacity ?? 1,
  };
}

/**
 * What `makeLayer` actually writes. It differs from `EditorLayer` in two places where the
 * reference stores an explicit `null` — `fieldKey` on a static/image layer and `crop` — and the
 * contract types them as absent instead. The null is kept, because it is what the seed carries
 * and what a saved draft is compared against.
 */
interface RawLayer {
  id: string;
  kind: string;
  fieldKey: string | null;
  x: number;
  y: number;
  width: number;
  height: number | null;
  rotation: number;
  zIndex: number;
  opacity: number;
  locked: boolean;
  hidden: boolean;
  safeAreaId: string | null;
  text?: string;
  font?: string;
  weight?: number;
  style?: string;
  fontSizeRatio?: number;
  minFontSizeRatio?: number;
  color?: string;
  align?: string;
  verticalAlign?: string;
  lineHeight?: number;
  letterSpacing?: number;
  maxLines?: number;
  fitMode?: string;
  textTransform?: string;
  assetRef?: EditorAssetRef | null;
  imageFit?: string;
  crop?: unknown;
}

/**
 * `makeLayer` — `m02-mockdb.js:137`. Every default is the reference's, including the ones the
 * property panel never shows: a text layer is 600 weight and `shrink` fit, a QR layer carries only
 * a colour, and an image layer's reference is rebuilt to `assetId` + `sha256` alone.
 */
export function makeLayer(o: Partial<EditorLayer> = {}): EditorLayer {
  const kind = o.kind || 'text';
  const base: RawLayer = {
    id: o.id || uid('lyr'),
    kind,
    fieldKey: o.fieldKey || null,
    x: o.x ?? 0.5,
    y: o.y ?? 0.5,
    width: o.width ?? 0.6,
    height: o.height ?? null,
    rotation: o.rotation ?? 0,
    zIndex: o.zIndex ?? 10,
    opacity: o.opacity ?? 1,
    locked: !!o.locked,
    hidden: !!o.hidden,
    safeAreaId: o.safeAreaId || null,
  };
  if (kind === 'text' || kind === 'static') {
    Object.assign(base, {
      text: o.text || '',
      font: o.font || 'Poppins',
      weight: o.weight ?? 600,
      style: o.style || 'normal',
      fontSizeRatio: o.fontSizeRatio ?? 0.04,
      minFontSizeRatio: o.minFontSizeRatio ?? 0.024,
      color: o.color || '#000000',
      align: o.align || 'center',
      verticalAlign: o.verticalAlign || 'middle',
      lineHeight: o.lineHeight ?? 1.15,
      letterSpacing: o.letterSpacing ?? 0,
      maxLines: o.maxLines ?? 1,
      fitMode: o.fitMode || 'shrink',
      textTransform: o.textTransform || 'none',
    });
  }
  if (kind === 'qr') Object.assign(base, { color: o.color || '#000000' });
  if (kind === 'image') {
    Object.assign(base, {
      assetRef: o.assetRef ? { assetId: o.assetRef.assetId, sha256: o.assetRef.sha256 } : null,
      imageFit: o.imageFit || 'contain',
      crop: o.crop || null,
    });
  }
  return base as unknown as EditorLayer;
}

/** `textLayer` — `m02-mockdb.js:153`. */
export const textLayer = (fieldKey: string, o: Partial<EditorLayer> = {}): EditorLayer =>
  makeLayer({ kind: 'text', fieldKey, ...o });

/** `qrLayer` — `m02-mockdb.js:154`. 8% of the canvas unless the caller says otherwise. */
export const qrLayer = (o: Partial<EditorLayer> = {}): EditorLayer =>
  makeLayer({ kind: 'qr', fieldKey: 'verification_qr', width: 0.08, ...o });

/** What `makeSafeArea` writes — `EditorSafeArea` plus the field types the area accepts. */
export interface EditorSafeAreaFull extends EditorSafeArea {
  allowedFieldTypes: string[];
}

/** `makeSafeArea` — `m02-mockdb.js:155`. The default type list excludes graphic types on purpose. */
export const makeSafeArea = (o: Partial<EditorSafeAreaFull> = {}): EditorSafeAreaFull => ({
  id: o.id || uid('safe'),
  label: o.label || 'Güvenli alan',
  x: o.x ?? 0.1,
  y: o.y ?? 0.1,
  width: o.width ?? 0.8,
  height: o.height ?? 0.2,
  allowedFieldTypes: o.allowedFieldTypes || ['string', 'text', 'date', 'number', 'integer', 'enum'],
});

// ------------------------------------------------------------------------------ the working draft

/**
 * `DRAFT_KEYS` — `m02-mockdb.js:181`. The whole list, in the reference's order: what is not named
 * here is release identity (`id`, `releaseNumber`, `releaseHash`, `issuerProfileSnapshot`…), which
 * a draft has not earned and must not inherit when the editor is opened on a published release.
 */
const DRAFT_KEYS = [
  'credentialType',
  'customTypeLabel',
  'subjectType',
  'holderField',
  'defaultLanguage',
  'localizedMetadata',
  'validityPolicy',
  'publicationPolicy',
  'permissions',
  'duplicatePolicy',
  'emailDefaults',
  'interoperability',
  /* Not in the reference's own list: the optional LinkedIn issuing organisation is a v0.7 product
     addition, and it has to travel with the draft or «Taslağı kaydet» would drop it. */
  'linkedInIssuer',
  'canvas',
  'background',
  /* Also not in the reference's own list, and for the same reason as `linkedInIssuer`: which
     languages a document is sealed in, and the artwork each of them draws on, are v0.7 product
     additions. Left out here, «Taslağı kaydet» would drop them and the second language would
     silently stop being configured every time the editor was opened. */
  'localizedBackground',
  'sealedLanguages',
  'fieldDefinitions',
  'layers',
  'safeAreas',
  'renderProfile',
] as const;

export type DraftSource = DemoDraft | DemoRelease | EditorLayout;

/**
 * `stripDraft` — `m02-mockdb.js:193`. A deep copy keyed to `DRAFT_KEYS`, so the object the editor
 * mutates is never the template's own: an unsaved experiment cannot reach the list screen, and
 * «Taslak kaydet» is the only thing that writes back.
 */
export function stripDraft(src: DraftSource | null | undefined): EditorLayout {
  const from = src as Record<string, unknown> | null | undefined;
  const out: Record<string, unknown> = {};
  for (const k of DRAFT_KEYS) {
    if (from && from[k] !== undefined) out[k] = deepClone(from[k]);
  }
  return out as unknown as EditorLayout;
}

/**
 * The render engine spells a layout's field definitions with non-nullable `labels`, the seed
 * spells them `labels: string | null`. Same objects, one nominal difference, so the editor crosses
 * from its own layout to the engine's here rather than at every call site.
 */
export const asRenderLayout = (layout: EditorLayout): Layout => layout as unknown as Layout;

/**
 * The same layout as ONE LANGUAGE draws it.
 *
 * The editor holds one design. What a language changes is narrow and is exactly what the server
 * changes when it seals that language's document (`domain/issuance/locale.ts`):
 *
 *   · the literal on a static text layer becomes `textLocalized[locale]`
 *   · the artwork becomes `localizedBackground[locale]`
 *
 * Geometry, fonts, colours, fields, safe areas and the canvas are shared and are not touched — there
 * is no per-language geometry in this product.
 *
 * The DECLARED language is returned unchanged, because that is what the server does: its document
 * draws the release's own canonical words on the release's own artwork. So the boxes the editor has
 * always had are the declared language's boxes, and a second language's boxes appear beside them.
 *
 * Nothing is substituted. A language with no artwork of its own gets NO artwork here, so the stage
 * shows what that language would actually be sealed as rather than quietly showing the other
 * language's picture under its name.
 */
export function localizedEditorLayout(layout: EditorLayout, locale: 'tr' | 'en'): EditorLayout {
  if (locale === layout.defaultLanguage) return layout;
  const localized = layout.localizedBackground?.[locale] ?? null;
  return {
    ...layout,
    /* This language's picture, with the release's own placement — one geometry, two pictures. */
    background:
      localized === null
        ? null
        : {
            ...(layout.background ?? {}),
            ...localized,
            assetRef: (localized as { assetRef?: unknown }).assetRef,
          },
    layers: layout.layers.map((l) => {
      if (l.kind !== 'static') return l;
      const said = l.textLocalized?.[locale];
      return { ...l, text: typeof said === 'string' ? said : '' };
    }),
  };
}

/**
 * Whether a working language edits the release's OWN artwork and words, or its own second-language
 * entry. The one expression behind that decision, so a reader and a writer cannot disagree about it.
 *
 * `localizedEditorLayout` above answers the reading side with it; every writer has to answer the
 * writing side with the same thing. When they diverged, the top bar's «Arka plan yükle» wrote
 * `background` while English was being edited: the Turkish document changed and English stayed
 * blank. Nothing here says which language is primary — `defaultLanguage` does.
 */
export const editsDeclaredLanguage = (layout: EditorLayout, locale: 'tr' | 'en'): boolean =>
  locale === layout.defaultLanguage;

/**
 * Writes ONE language's artwork entry and never another language's.
 *
 * The bag is rebuilt rather than mutated in place, so an undo step holds the bag the draft had; and
 * only the named key is touched, so the other language's picture survives every write. `null`
 * removes this language's entry, which is «this language has no artwork of its own» — never a
 * fallback to the other language's.
 *
 * The entry is the asset REFERENCE (and the alpha answer where it is known), not a placement: fit,
 * focal point, crop, scale, opacity and rotation are the release's own and are shared — one
 * geometry, two pictures (`localizedBackgroundToApi`).
 */
export function setLocaleBackground(
  working: EditorLayout,
  locale: 'tr' | 'en',
  entry: { assetRef: EditorAssetRef; hasAlpha?: boolean } | null,
): void {
  const bag = { ...(working.localizedBackground ?? {}) };
  if (entry === null) delete bag[locale];
  else bag[locale] = entry as unknown as Record<string, unknown>;
  working.localizedBackground = bag;
}

// ---------------------------------------------------------------------------- editor accessors

/** `layerById` — `m08-admin.js:166`. */
export const layerById = (layout: EditorLayout | null, id: string | null): EditorLayer | null =>
  (layout && id ? layout.layers.find((l) => l.id === id) : null) ?? null;

/** `edField` — `m08-admin.js:168`. */
export const edField = (
  layout: EditorLayout | null,
  key: string | null | undefined,
): DemoFieldDefinition | null => (layout?.fieldDefinitions ?? []).find((f) => f.key === key) ?? null;

// -------------------------------------------------------------------------------- test data chips

/** `TEST_NAMES` — `m01b-assets.js:570`. The long ones exist to make a layer shrink; keep them long. */
export const TEST_NAMES = [
  'Ali Can',
  'Ayşe Yılmaz',
  'Mehmet Ali Büyükdemircioğlu',
  'Alexandra Konstantinopoulou-Wetherington',
  'İbrahim Çağrı Şentürk',
];

/** `TEST_ORGS` — `m01b-assets.js:571`. */
export const TEST_ORGS = [
  'ABC Holding',
  'Kolektif Alan',
  'Uluslararası Anadolu Lojistik ve Dağıtım Hizmetleri A.Ş.',
  'XYZ Teknoloji',
  'Şerbet Gıda ve İçecek Sanayi',
];

/**
 * `defaultTestData` — `m08-admin.js:169`, which is `sampleData(t, 'short', lay)`.
 *
 * The values are strings by the time the panel renders them (`sampleData` falls through sample →
 * default → label → key, never to nothing), so the contract's `Record<string, string>` is the same
 * data and not a narrowing of it.
 */
export function defaultTestData(t: DemoTemplate, lay: EditorLayout): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(sampleData(t, 'short', asRenderLayout(lay)))) {
    out[k] = v == null ? '' : String(v);
  }
  return out;
}

// ------------------------------------------------------------------------------------- font lists

/** `fontFamilyList` — `m01-utils.js:162`. Falls back to Poppins so the select is never empty. */
export function fontFamilyList(): string[] {
  const fams = [...new Set(seedFonts().map((f) => f.family))];
  return fams.length ? fams : ['Poppins'];
}

/** `fontWeightList` — `m01-utils.js:163` over `FontLibrary.weightsOf`: the family's real faces, ascending. */
export function fontWeightList(family: string): number[] {
  const w = [
    ...new Set(
      seedFonts()
        .filter((f) => f.family === family)
        .map((f) => f.weight),
    ),
  ].sort((a, b) => a - b);
  return w.length ? w : [400, 500, 600, 700];
}
